import { RefObject, UIEvent, useEffect, useRef, useState } from 'react'

const SCROLL_TOP_MARGIN = 200

export const scrollbarXVisible = (element: HTMLDivElement) => {
  return element.scrollWidth > element.clientWidth
}

export const scrollbarYVisible = (element: HTMLDivElement) => {
  return element.scrollHeight > element.clientHeight
}

export const useWindowScrolledTo = (
  ref: RefObject<HTMLElement | null>,
  threshold: number,
  endThreshold: number = 0
) => {
  const [isScrolled, setIsScrolled] = useState<boolean>(false)
  const [isScrolledStart, setIsScrolledStart] = useState<boolean>(false)
  const [isScrolledEnd, setIsScrolledEnd] = useState<boolean>(false)

  useEffect(() => {
    const handleScroll = () => {
      const elementTop = ref.current ? ref.current.getBoundingClientRect().top : 0
      const elementBottom = ref.current ? ref.current.getBoundingClientRect().bottom : 0

      if (elementTop) {
        setIsScrolled(elementTop < threshold)
        setIsScrolledStart(elementTop + threshold < window.innerHeight)
        setIsScrolledEnd(elementBottom + endThreshold < window.innerHeight)
      }
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [ref])

  return { isScrolled, isScrolledStart, isScrolledEnd }
}

export const useScrollHorizon = <T extends HTMLElement>(tableRef: RefObject<HTMLTableElement>) => {
  const headFloatRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const [isScrolled, setIsScrolled] = useState<boolean>(false)

  const onScrollTable = (event: UIEvent<T>) => {
    headFloatRef.current?.scrollTo(event.currentTarget.scrollLeft, 0)
    barRef.current?.scrollTo(event.currentTarget.scrollLeft, 0)
    setIsScrolled(event.currentTarget.scrollLeft > 0)
  }

  const onScrollBar = (event: UIEvent<T>) => {
    headFloatRef.current?.scrollTo(event.currentTarget.scrollLeft, 0)
    tableRef.current?.scrollTo(event.currentTarget.scrollLeft, 0)
    setIsScrolled(event.currentTarget.scrollLeft > 0)
  }

  return { onScrollTable, onScrollBar, headFloatRef, barRef, isScrolled }
}

export const handleSmoothScroll = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
  event.preventDefault()
  const targetId = event.currentTarget.getAttribute('href')?.substring(1)
  const targetElement = document.getElementById(targetId || '')

  if (targetElement) {
    const headerHeight = document.querySelector('header')?.clientHeight || 0
    const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY

    window.scrollTo({
      top: targetPosition - headerHeight,
      behavior: 'smooth'
    })
  }
}

export const scrollToId = (linkId: string) => {
  const element: HTMLElement | null = document.getElementById(linkId)
  if (element) {
    const rect = element.getBoundingClientRect()
    window.scrollTo({ top: rect.top - SCROLL_TOP_MARGIN, behavior: 'smooth' })
  }
}
